/* eslint-disable */

/**
 * reviewLanding — public patient rating page (/rv/:shortId) state + API calls.
 * Reviews milestone A4; canonical plan: Documents/teamio-reviews.md.
 *
 * Mirrors the txPresenter patientView pattern (TxPatientView.vue): the page is
 * public (no admin auth) — the opaque shortId in the URL is the only token, and
 * every endpoint resolves identity server-side in reviews-service.
 *
 * ⚖️ NUDGED COMPLIANT GATE: the backend returns ALL enabled review links in
 * BOTH branches (public and private). This module never filters them by
 * rating — prominence is a presentation choice in ReviewLanding.vue, and the
 * public Google option must remain reachable from every branch.
 */

import axios from 'axios';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

export default {
  namespaced: true,

  state: {
    loading: false,
    // 'expired' → friendly "link expired" page (404/410); 'error' → generic failure
    errorType: null,
    error: null,
    shortId: null,
    // Payload from /get-review-landing:
    // { practiceName, practiceLogoUrl, patientFirstName, gateThreshold,
    //   reviewLinks: { google|facebook|yelp: { url, verifiedAt } },
    //   reasons: [], alreadyCompleted, rating }
    landing: null,
  },

  mutations: {
    SET_LOADING(state, v) { state.loading = v; },
    SET_ERROR(state, { errorType, error }) {
      state.errorType = errorType;
      state.error = error;
    },
    SET_LANDING(state, { shortId, data }) {
      state.shortId = shortId;
      state.landing = data;
      state.errorType = null;
      state.error = null;
    },
    // /submit-review-rating echoes the (server-truth) link set + reasons —
    // refresh them so a settings edit between page load and rating wins.
    REFRESH_GATE_DATA(state, data) {
      if (!state.landing || !data) return;
      state.landing = {
        ...state.landing,
        gateThreshold: data.gateThreshold !== undefined ? data.gateThreshold : state.landing.gateThreshold,
        reviewLinks: data.reviewLinks || state.landing.reviewLinks,
        reasons: data.reasons || state.landing.reasons,
      };
    },
  },

  actions: {
    /** First paint: branding + first name + gate + links + reasons. */
    async loadLanding({ commit }, { shortId }) {
      if (!shortId) throw new Error('shortId is required');
      commit('SET_LOADING', true);
      commit('SET_ERROR', { errorType: null, error: null });
      try {
        const response = await axios.post('/get-review-landing', { shortId }, { crossdomain: true });
        if (response.data && response.data.success) {
          commit('SET_LANDING', { shortId, data: response.data.data });
          return response.data.data;
        }
        throw new Error((response.data && response.data.message) || 'Failed to load');
      } catch (err) {
        console.error('[reviewLanding] loadLanding error:', err);
        const status = err.response && err.response.status;
        // 404 (unknown) and 410 (expired) both mean "this link is done" to the patient.
        const errorType = (status === 404 || status === 410) ? 'expired' : 'error';
        commit('SET_ERROR', {
          errorType,
          error: errorType === 'expired'
            ? 'This link is no longer active.'
            : 'Something went wrong loading this page.',
        });
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Store the star rating; returns { branch, gateThreshold, reviewLinks, reasons }.
     * The response carries ALL enabled links regardless of branch (compliance).
     */
    async submitRating({ state, commit }, { rating }) {
      const response = await axios.post(
        '/submit-review-rating',
        { shortId: state.shortId, rating },
        { crossdomain: true }
      );
      if (response.data && response.data.success) {
        commit('REFRESH_GATE_DATA', response.data.data);
        return response.data.data;
      }
      throw new Error((response.data && response.data.message) || 'Failed to submit rating');
    },

    /** Private branch submit: reasons chips + optional note → office inbox + email. */
    async submitFeedback({ state }, { rating, reasons, note }) {
      const response = await axios.post(
        '/submit-private-feedback',
        { shortId: state.shortId, rating, reasons, note },
        { crossdomain: true }
      );
      if (response.data && response.data.success) return response.data.data;
      throw new Error((response.data && response.data.message) || 'Failed to send feedback');
    },

    /**
     * Fire-and-forget outcome tracking as the patient leaves for the public
     * platform. Never awaited by the UI and never allowed to block the redirect.
     */
    trackOutcome({ state }, { outcome }) {
      axios.post(
        '/track-review-outcome',
        { shortId: state.shortId, outcome },
        { crossdomain: true }
      ).catch((err) => {
        console.warn('[reviewLanding] trackOutcome failed (non-blocking):', err && err.message);
      });
    },
  },
};
